import {Injectable} from '@angular/core';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {GridActionButtonsHelper} from '@application/helper/grid-action-buttons-helper/grid-action-buttons-helper';
import {PageTypeEnum} from '@application/helper/navigation-helper/page-type.enum';
import {AddComponentInput} from '@application/helper/routing/add-component-input.interface';
import {NavigationId} from '@application/helper/routing/navigation-id.enum';
import {NavigationUtils} from '@application/helper/routing/navigation-utils';
import {Permission} from '@domain/profile/permission.enum';
import {PlasticProduct} from '@domain/textile-data/plastic-product/plastic-product';
import {HttpPlasticProductsService} from '@infrastructure/http/plastic-product/http-plastic-products.service';
import {
  ContentSwitcherComponentData,
  CrudObjectSelectionDialogComponent,
  CrudObjectSelectionDialogInput,
  CrudOverviewDataBuilderFactoryService,
  CrudOverviewDataConfig,
  GridOptionsBuilder,
  NavigationHelperPrototypeService
} from '@vdw/angular-component-library';
import {Observable} from 'rxjs';
import {PlasticProductOverviewService} from './overview/plastic-product-overview.service';
import {PlasticProductModule} from './plastic-product.module';

@Injectable({providedIn: 'root'})
export class PlasticProductSelectionService {
  private config: CrudOverviewDataConfig<PlasticProduct>;
  private readonly TRANSLATION_KEY = 'TEXTILE_DATA.PLASTIC_PRODUCT.PLASTIC_PRODUCT';

  public constructor(
    private readonly plasticProducts: HttpPlasticProductsService,
    private readonly overview: PlasticProductOverviewService,
    private readonly navigationHelper: NavigationHelperPrototypeService,
    private readonly builderFactoryService: CrudOverviewDataBuilderFactoryService<PlasticProduct>,
    private readonly gridActionButtonsHelper: GridActionButtonsHelper,
    private readonly plasticProductService: HttpPlasticProductsService
  ) {}

  public init(enableMultiDelete: boolean, isProductLocked?: (id: number) => boolean): void {
    const crudOverviewButtonConfigs = this.gridActionButtonsHelper.getActionButtonsWithRouting(
      NavigationUtils.getNavigationRoute(NavigationId.ADD_PLASTIC_PRODUCT).component,
      Permission.TEXSTYLE_PLASTIC_PRODUCT_EDIT,
      this.gridActionButtonsHelper.getCrudOverviewButtonConfigs(
        () => this.config.selectedObjects,
        (id: number) => this.plasticProductService.delete(id),
        this.TRANSLATION_KEY
      ),
      PlasticProductModule
    );

    this.config = this.builderFactoryService
      .getBuilder()
      .withEntityName(this.TRANSLATION_KEY)
      .withRowData(this.plasticProducts.getAll())
      .withDefaultButtons(crudOverviewButtonConfigs)
      .withCrudGridOptions(
        this.overview.getColumnDefs(true, undefined, PlasticProductModule, crudOverviewButtonConfigs.rowActionsComplete),
        GridIdentifier.PLASTIC_PRODUCT_OVERVIEW,
        null,
        (builder: GridOptionsBuilder) =>
          this.overview.getDefaultGridOptionsBuilder(builder).withRowSelection(enableMultiDelete, true, true).withDisableDeselection(isProductLocked).withLockUsedItem().build()
      )
      .build();
  }

  public openContentSwitcherDialog(inputValue: PlasticProduct[], data: any, toolId?: number, machineId?: number, machineGroupId?: number): Observable<PlasticProduct[]> {
    this.config.rowData = null;
    this.config.selectedObjects = inputValue;

    this.config.dataObservable = this.plasticProducts.getCompatible(toolId, machineId, machineGroupId);

    return this.navigationHelper.navigateWithContentSwitcher<PlasticProduct[]>({
      componentInfo: {
        component: CrudObjectSelectionDialogComponent,
        componentInputs: {
          config: this.config,
          newClickedEntry: {
            componentInfo: {
              component: NavigationUtils.getNavigationRoute(NavigationId.ADD_PLASTIC_PRODUCT).component,
              module: PlasticProductModule,
              componentInputs: {pageType: PageTypeEnum.ADD, canShowHeader: true} as AddComponentInput
            } as ContentSwitcherComponentData
          }
        } as CrudObjectSelectionDialogInput
      }
    });
  }
}
